import { typographyTokens } from '../../../theme/typographyTokens';
import React, { useState, useCallback, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Table, TableBody, TableCell, tableCellClasses, TableContainer,
  TableHead, TableRow, Paper, Box, Typography, TablePagination, TextField,
  FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Button
} from '@mui/material';
import SpinnerOverlay from '../../../components/Spinner/Spinner';
import { useGetAPILogsQuery, useGetAPILogRequestMethodsQuery } from '../../../services/appSlice/appSlice';
import { APILogEntries as APILogsEntryType } from '../../../../shared/types/APILogEntries';

// Date picker and dayjs imports
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

// Constants for min and max dates
const MIN_DATE = dayjs('2025-01-01');
const MAX_DATE = dayjs(); // today

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
}));

// Table Header Cells
const TABLE_HEADERS = [
  { label: 'ID', width: '3%' },
  { label: 'Requested Date', width: '12%' },
  { label: 'Requestor Name', width: '12%' },
  { label: 'Requestor Email', width: '15%' },
  { label: 'Screen Name', width: '12%' },
  { label: 'Request URL', width: '18%' },
  { label: 'Status', width: '5%' },
  { label: 'Response Time', width: '8%' },
  { label: 'Request Method', width: '10%' },
  { label: 'Action', width: '5%' },
];

// Date Range Filter Props
interface DateRangeFilterProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
}

// Date Range Filter Component using MUI DatePicker
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate, endDate, onStartDateChange, onEndDateChange,
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <Box display="flex" gap={2}>
      <DatePicker
        label="Start Date"
        value={startDate}
        onChange={onStartDateChange}
        minDate={MIN_DATE}
        maxDate={endDate || MAX_DATE}
        slotProps={{
          textField: {
            placeholder: 'mm/dd/yyyy',
            size: 'small',
            fullWidth: true,
          },
        }}
      />
      <DatePicker
        label="End Date"
        value={endDate}
        onChange={onEndDateChange}
        minDate={startDate || MIN_DATE}
        maxDate={MAX_DATE}
        slotProps={{
          textField: {
            placeholder: 'mm/dd/yyyy',
            size: 'small',
            fullWidth: true,
          },
        }}
      />
    </Box>
  </LocalizationProvider>
);

// Main Table Component
export default function APILogEntries() {
  // State for pending filters (before Apply)
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [requestMethodFilter, setRequestMethodFilter] = useState<string>('');
  
  // State for applied filters (sent to API)
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [appliedStartDate, setAppliedStartDate] = useState<Dayjs | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Dayjs | null>(null);
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>('');
  const [appliedRequestMethodFilter, setAppliedRequestMethodFilter] = useState<string>('');

  // Fetch request methods for dropdown
  const { data: requestMethods = [] } = useGetAPILogRequestMethodsQuery();

  // API call with server-side pagination, search, and date filters (1-indexed for backend)
  const {
    data: apiResponse,
    isError,
    isLoading,
    isFetching,
  } = useGetAPILogsQuery({ 
    page: page + 1, 
    limit: rowsPerPage, 
    search: appliedSearch,
    startDate: appliedStartDate?.format('YYYY-MM-DD'),
    endDate: appliedEndDate?.format('YYYY-MM-DD'),
    status: appliedStatusFilter || undefined,
    requestMethodName: appliedRequestMethodFilter || undefined,
  });

  // Handlers
  const handleStartDateChange = useCallback((date: Dayjs | null) => {
    setStartDate(date);
    if (endDate && date && date.isAfter(endDate, 'day')) setEndDate(null);
  }, [endDate]);

  const handleEndDateChange = useCallback((date: Dayjs | null) => {
    setEndDate(date);
    if (startDate && date && date.isBefore(startDate, 'day')) setStartDate(date);
  }, [startDate]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value), []);
  
  const handleStatusChange = useCallback((e: SelectChangeEvent<string>) => setStatusFilter(e.target.value), []);
  
  const handleRequestMethodChange = useCallback((e: SelectChangeEvent<string>) => setRequestMethodFilter(e.target.value), []);

  // Apply filters handler
  const handleApplyFilters = useCallback(() => {
    setAppliedSearch(searchTerm);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedStatusFilter(statusFilter);
    setAppliedRequestMethodFilter(requestMethodFilter);
    setPage(0); // Reset to first page
  }, [searchTerm, startDate, endDate, statusFilter, requestMethodFilter]);

  // Reset filters handler
  const handleResetFilters = useCallback(() => {
    // Reset pending filters
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setStatusFilter('');
    setRequestMethodFilter('');
    // Reset applied filters
    setAppliedSearch('');
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    setAppliedStatusFilter('');
    setAppliedRequestMethodFilter('');
    setPage(0); // Reset to first page
  }, []);

  // Data from API (all filtering is now server-side)
  const filteredAPILogs: APILogsEntryType[] = apiResponse?.data || [];

  // Total records from server pagination
  const totalRecords = apiResponse?.pagination?.totalRecords || 0;

  // Loading/Error States
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} py="24px">
        <SpinnerOverlay />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} py="24px">
        <Typography color="error.main" fontSize={typographyTokens.fontSizes.h5}>Error</Typography>
      </Box>
    );
  }

  // Main Render
  return (
    <>
      {/* Filters */}
      <Box display="flex" flexDirection="column" gap={2} mb={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="others">Others</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="request-method-filter-label">Request Method</InputLabel>
            <Select
              labelId="request-method-filter-label"
              value={requestMethodFilter}
              label="Request Method"
              onChange={handleRequestMethodChange}
            >
              <MenuItem value="">All</MenuItem>
              {requestMethods.map((method) => (
                <MenuItem key={method} value={method}>{method}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Search by Requestor Name or Email"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 300, flexGrow: 1, maxWidth: 500 }}
            inputProps={{ maxLength: 100 }}
          />
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyFilters}
              size="small"
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleResetFilters}
              size="small"
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Box>
      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 470, minHeight: 470, position: 'relative' }}>
        {isFetching && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
            <SpinnerOverlay />
          </Box>
        )}
        <Table stickyHeader sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <StyledTableCell key={header.label} sx={{ width: header.width }}>
                  {header.label}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAPILogs.length ? filteredAPILogs.map((row: APILogsEntryType, index: number) => (
              <StyledTableRow key={row.id || index}>
                <StyledTableCell sx={{ width: '3%' }}>{row.id}</StyledTableCell>
                <StyledTableCell sx={{ width: '12%' }}>
                  {row.createdAt ? dayjs(row.createdAt).format('MM/DD/YYYY HH:mm:ss') : '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ width: '12%' }}>{row.requestorName || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '15%' }}>{row.requestorEmail || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '12%' }}>{row.screenName || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '18%', wordBreak: 'break-all' }}>{row.requestUrl || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '5%', color: row.statusCode && row.statusCode >= 400 ? 'error.main' : 'inherit' }}>
                  {row.statusCode || '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ width: '8%' }}>
                  {row.responseTimeMs != null ? `${row.responseTimeMs} ms` : '-'}
                </StyledTableCell>
                <StyledTableCell sx={{ width: '10%' }}>{row.requestMethodName || '-'}</StyledTableCell>
                <StyledTableCell sx={{ width: '5%' }}>{row.action || '-'}</StyledTableCell>
              </StyledTableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography color="error.main" fontSize={typographyTokens.fontSizes.xl} padding="17px 0PX 0px 20px">
                    No record found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Box sx={{ margin: '16px 0px 10px 0px' }}>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </>
  );
}
 